import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ComparativeAnalysisProps {
  reportId: string;
  clientLoss: number;
  eventType: string;
}

interface Comparison {
  industry_avg: number;
  similar_claims_avg: number;
  percentile: number;
  variance_from_avg_pct: number;
}

interface Insight {
  type: 'positive' | 'neutral' | 'negative';
  message: string;
  icon: string;
}

export default function ComparativeAnalysis({ reportId, clientLoss, eventType }: ComparativeAnalysisProps) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState<any>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadAnalysis();
  }, [reportId]);

  const loadAnalysis = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/benchmarks/analysis/${reportId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAnalysis(response.data.analysis);
      setLoading(false);
    } catch (err: any) {
      console.error('Comparative analysis error:', err);
      setError(err.response?.data?.detail || 'Failed to load comparative analysis');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border-2 border-blue-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">📊</span>
          <h3 className="text-lg font-bold text-gray-900">Comparative Analysis</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading industry benchmarks...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">📊</span>
          <h3 className="text-lg font-bold text-gray-900">Comparative Analysis</h3>
          <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-bold">PREVIEW</span>
        </div>
        <p className="text-sm text-gray-600">
          Comparative analysis will show how your claim compares to similar {eventType} claims 
          and industry benchmarks. Currently in development.
        </p>
      </div>
    );
  }

  if (!analysis) return null;

  const { comparisons, percentile, insights } = analysis;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">📊</span>
          <h3 className="text-lg font-bold text-gray-900">Comparative Analysis</h3>
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold">INDUSTRY BENCHMARKS</span>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
        >
          {expanded ? '▼ Hide Details' : '▶ Show Details'}
        </button>
      </div>

      {/* Quick Summary */}
      <div className="bg-white rounded-lg p-4 mb-4">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Your Loss */}
          <div>
            <p className="text-xs text-gray-600 mb-1">Your Calculated Loss:</p>
            <p className="text-2xl font-bold text-gray-900">${clientLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>

          {/* Industry Average */}
          <div>
            <p className="text-xs text-gray-600 mb-1">Industry Average ({eventType}):</p>
            <p className="text-2xl font-bold text-blue-700">
              ${comparisons?.industry_avg?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
            </p>
          </div>

          {/* Percentile */}
          <div>
            <p className="text-xs text-gray-600 mb-1">Your Percentile Rank:</p>
            <p className="text-2xl font-bold text-indigo-700">
              {percentile?.value ? `${percentile.value}th` : 'N/A'}
            </p>
            <p className="text-xs text-gray-500">
              {percentile?.value && percentile.value > 75 && 'Higher than 75% of claims'}
              {percentile?.value && percentile.value <= 75 && percentile.value > 50 && 'Above median'}
              {percentile?.value && percentile.value <= 50 && 'Below median'}
            </p>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-white rounded-lg p-4">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <span>💡</span>
          Key Insights
        </h4>
        <div className="space-y-2">
          {insights?.map((insight: Insight, idx: number) => (
            <div
              key={idx}
              className={`p-3 rounded-lg border-l-4 ${
                insight.type === 'positive' ? 'bg-green-50 border-green-500' :
                insight.type === 'negative' ? 'bg-red-50 border-red-500' :
                'bg-blue-50 border-blue-500'
              }`}
            >
              <p className="text-sm text-gray-800">
                <span className="mr-2">{insight.icon}</span>
                {insight.message}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && comparisons && (
        <div className="mt-4 bg-white rounded-lg p-4">
          <h4 className="font-bold text-gray-900 mb-3">Detailed Comparison</h4>
          
          <div className="space-y-3">
            {/* Similar Claims Comparison */}
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-sm font-semibold text-gray-700">Similar Claims Average:</p>
              <p className="text-lg font-bold text-gray-900">
                ${comparisons.similar_claims_avg?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Based on {comparisons.sample_size || 0} similar {eventType} claims
              </p>
            </div>

            {/* Variance from Average */}
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-sm font-semibold text-gray-700">Variance from Industry Average:</p>
              <p className={`text-lg font-bold ${
                comparisons.variance_from_avg_pct > 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {comparisons.variance_from_avg_pct > 0 ? '+' : ''}
                {comparisons.variance_from_avg_pct?.toFixed(1)}%
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {comparisons.variance_from_avg_pct > 0 ? 'Above' : 'Below'} industry average
              </p>
            </div>

            {/* Context */}
            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-900">
                <strong>Note:</strong> Comparative analysis provides context for your claim. 
                Higher percentiles don't necessarily indicate issues - they may reflect 
                unique circumstances, larger business size, or more severe event impact.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
